/**
 * ResetPasswordPage - Password Reset Flow
 *
 * Migrated to BasePage architecture.
 * Demonstrates: Loading states, error states, form with Input component integration.
 */
import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { confirmPasswordReset, verifyPasswordResetCode } from "firebase/auth";
import { Eye, EyeOff, Lock, Loader2 } from "lucide-react";

// Hooks & Contexts
import { useTranslation } from "@/shared/hooks";
import { usePasswordValidation } from "@/features/auth/hooks";

// New Design System
import { BasePage } from "@/shared/ui/layout/BasePage";
import { Box, Text } from "@/shared/ui/base";
import { Card, Button, Input } from "@/shared/ui/components";

// Components
import { PasswordRequirement } from "@/features/auth/components";

// Lib
import { auth } from "@/shared/lib/firebase";

// Constants
import { AppRoutes } from "@/shared/constants";

type ResetStatus =
  | "checking"
  | "ready"
  | "submitting"
  | "success"
  | "error"
  | "missing_code";

export default function ResetPasswordPage() {
  const t = useTranslation();
  const navigate = useNavigate();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const oobCode = params.get("oobCode");

  const [status, setStatus] = useState<ResetStatus>("checking");
  const [errorType, setErrorType] = useState<"expired" | "invalid" | "generic">(
    "generic",
  );
  const [userEmail, setUserEmail] = useState<string | null>(null);

  // Form state
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);

  // Password validation
  const passwordValidation = usePasswordValidation(password);

  const isFormValid =
    passwordValidation.isValid &&
    password === confirmPassword &&
    password.length > 0;

  // Verify the reset code on mount
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!oobCode) {
        if (mounted) setStatus("missing_code");
        return;
      }
      try {
        const email = await verifyPasswordResetCode(auth, oobCode);
        if (mounted) {
          setUserEmail(email);
          setStatus("ready");
        }
      } catch (e) {
        if (mounted) {
          const code = (e as { code?: string })?.code || "";
          if (code === "auth/expired-action-code") {
            setStatus("error");
            setErrorType("expired");
          } else if (code === "auth/invalid-action-code") {
            setStatus("error");
            setErrorType("invalid");
          } else {
            setStatus("error");
            setErrorType("generic");
          }
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, [oobCode]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!oobCode || !isFormValid) return;

      setStatus("submitting");
      try {
        await confirmPasswordReset(auth, oobCode, password);
        setStatus("success");
      } catch (err) {
        const code = (err as { code?: string })?.code || "";
        if (code === "auth/expired-action-code") {
          setErrorType("expired");
        } else if (code === "auth/weak-password") {
          setErrorType("generic");
        } else {
          setErrorType("generic");
        }
        setStatus("error");
      }
    },
    [oobCode, password, isFormValid],
  );

  return (
    <BasePage
      title={t("resetPasswordTitle")}
      description="Reset your password"
      maxWidth="sm"
      centerContent
      showHeader={false}
      showFooter={false}
    >
      {/* Success State */}
      {status === "success" && (
        <Card
          variant="default"
          padding="md"
          className="bg-emerald-50 border-emerald-200"
        >
          <Text variant="h4" className="text-emerald-800 mb-2">
            {t("resetPasswordSuccessTitle")}
          </Text>
          <Text variant="body" className="text-emerald-700 mb-4">
            {t("resetPasswordSuccessBody")}
          </Text>
          <Button
            variant="primary"
            fullWidth
            onClick={() => navigate(AppRoutes.login)}
          >
            {t("logIntoAccount")}
          </Button>
        </Card>
      )}

      {/* Checking State */}
      {status === "checking" && (
        <Box className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-red-600 mx-auto mb-4" />
          <Text variant="body" color="primary">
            {t("pleaseWait")}
          </Text>
        </Box>
      )}

      {/* Missing Code State */}
      {status === "missing_code" && (
        <Card
          variant="default"
          padding="md"
          className="bg-yellow-50 border-yellow-200"
        >
          <Text variant="h4" className="text-yellow-800 mb-2">
            {t("resetPasswordMissingCodeTitle")}
          </Text>
          <Text variant="body" className="text-yellow-700 mb-4">
            {t("resetPasswordMissingCodeBody")}
          </Text>
          <Button
            variant="primary"
            fullWidth
            onClick={() => navigate(AppRoutes.login)}
          >
            {t("backToLogin")}
          </Button>
        </Card>
      )}

      {/* Error State */}
      {status === "error" && (
        <Card
          variant="default"
          padding="md"
          className="bg-red-50 border-red-200"
        >
          <Text variant="h4" className="text-red-800 mb-2">
            {t("resetPasswordFailedTitle")}
          </Text>
          <Text variant="body" className="text-red-700 mb-4">
            {errorType === "expired"
              ? t("resetPasswordExpired")
              : errorType === "invalid"
                ? t("resetPasswordInvalid")
                : t("resetPasswordFailed")}
          </Text>
          <Button
            variant="primary"
            fullWidth
            onClick={() => navigate(AppRoutes.login)}
          >
            {t("backToLogin")}
          </Button>
        </Card>
      )}

      {/* Ready State - Form */}
      {(status === "ready" || status === "submitting") && (
        <Card variant="default" padding="md" className="shadow-sm">
          <Text variant="h2" className="mb-2">
            {t("resetPasswordTitle")}
          </Text>
          <Text variant="body" color="secondary" className="mb-6">
            {t("resetPasswordSubtitle")}
            {userEmail && (
              <Text
                as="span"
                weight="medium"
                color="primary"
                className="block mt-1"
              >
                {userEmail}
              </Text>
            )}
          </Text>

          <Box as="form" onSubmit={handleSubmit} className="space-y-4">
            {/* New Password Input */}
            <Box>
              <Input
                label={t("resetPasswordNewPassword")}
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setPasswordFocused(true)}
                onBlur={() => setPasswordFocused(false)}
                icon={<Lock className="w-5 h-5 text-gray-400" />}
                placeholder="••••••••"
                autoComplete="new-password"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-[38px]"
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </Button>

              {/* Password Requirements */}
              {(passwordFocused || password.length > 0) && (
                <Box className="mt-2 p-3 bg-gray-50 rounded-lg">
                  <Text
                    variant="caption"
                    weight="medium"
                    color="primary"
                    className="mb-2"
                  >
                    {t("passwordRequirements")}
                  </Text>
                  <PasswordRequirement
                    text={t("passwordReqMin8")}
                    ok={passwordValidation.requirements.minLength}
                  />
                  <PasswordRequirement
                    text={t("passwordReqNumber")}
                    ok={passwordValidation.requirements.hasNumber}
                  />
                  <PasswordRequirement
                    text={t("passwordReqSymbol")}
                    ok={passwordValidation.requirements.hasSymbol}
                  />
                  <PasswordRequirement
                    text={t("passwordReqUpper")}
                    ok={passwordValidation.requirements.hasUppercase}
                  />
                  <PasswordRequirement
                    text={t("passwordReqLower")}
                    ok={passwordValidation.requirements.hasLowercase}
                  />
                </Box>
              )}
            </Box>

            {/* Confirm Password Input */}
            <Box className="relative">
              <Input
                label={t("confirmPassword")}
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                icon={<Lock className="w-5 h-5 text-gray-400" />}
                placeholder="••••••••"
                autoComplete="new-password"
                error={
                  confirmPassword && password !== confirmPassword
                    ? t("passwordsDontMatch")
                    : undefined
                }
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-2 top-[38px]"
                tabIndex={-1}
              >
                {showConfirmPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </Button>
            </Box>

            {/* Submit Button */}
            <Button
              type="submit"
              variant="primary"
              fullWidth
              disabled={!isFormValid || status === "submitting"}
              loading={status === "submitting"}
            >
              {status === "submitting"
                ? t("pleaseWait")
                : t("resetPasswordSubmit")}
            </Button>
          </Box>
        </Card>
      )}
    </BasePage>
  );
}
